import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import supabase from 'embroil/supabase';

import type { SearchResult, SearchResultType } from '../types/search';

interface RankingFactors {
  // Content quality factors
  recency: number; // How recent the content is
  authority: number; // Author/source authority score
  engagement: number; // User engagement metrics
  relevance: number; // Query relevance score
  popularity: number; // View count, shares, etc.

  // User behavior factors
  clickThroughRate: number; // CTR for this result
  dwellTime: number; // Average time spent on content
  bounceRate: number; // How often users bounce back

  // Content type factors
  typePreference: number; // User's preference for this content type
  seasonality: number; // Seasonal relevance (tournaments, etc.)
}

interface UserProfile {
  contentTypePreferences: Record<SearchResultType, number>;
  gamePreferences: Record<string, number>;
  authorPreferences: Record<string, number>;
  searchHistory: string[];
  clickHistory: { resultId: string; timestamp: number; dwellTime?: number }[];
}

interface RankingModel {
  weights: {
    recency: number;
    authority: number;
    engagement: number;
    relevance: number;
    popularity: number;
    clickThroughRate: number;
    dwellTime: number;
    bounceRate: number;
    typePreference: number;
    seasonality: number;
  };
  version: string;
  lastUpdated: string;
}

interface MetricsService {
  track(eventName: string, properties: Record<string, unknown>): void;
}

interface InteractionAnalysis {
  avgCTR: number;
  avgDwellTime: number;
  avgBounceRate: number;
  topPerformingFactors: string[];
}

export default class SearchRankingService extends Service {
  @service declare metrics: MetricsService;

  @tracked currentModel: RankingModel = this.getDefaultModel();
  @tracked isTraining = false;
  @tracked lastModelUpdate = '';

  // Default ranking model weights (can be machine learned)
  private getDefaultModel(): RankingModel {
    return {
      weights: {
        recency: 0.15, // Recent content gets boost
        authority: 0.2, // Authoritative sources rank higher
        engagement: 0.18, // High engagement content ranks higher
        relevance: 0.25, // Most important - query relevance
        popularity: 0.12, // Popular content gets boost
        clickThroughRate: 0.08, // Content users click on more
        dwellTime: 0.06, // Content users spend time on
        bounceRate: -0.04, // Negative - high bounce rate reduces rank
        typePreference: 0.1, // User's content type preferences
        seasonality: 0.05, // Seasonal content boost
      },
      version: '1.0.0',
      lastUpdated: new Date().toISOString(),
    };
  }

  /**
   * Rank search results based on ML model and user behavior
   */
  async rankResults(
    results: SearchResult[],
    query: string,
    userId?: string,
  ): Promise<SearchResult[]> {
    if (results.length === 0) return results;

    try {
      // Get user profile for personalization
      const userProfile = userId ? await this.getUserProfile(userId) : null;

      // Calculate ranking scores for each result
      const rankedResults = await Promise.all(
        results.map(async (result) => {
          const factors = await this.calculateRankingFactors(
            result,
            query,
            userProfile,
          );
          const score = this.calculateRankingScore(factors);

          return {
            ...result,
            relevanceScore: score,
            rankingFactors: factors, // For debugging/analysis
          };
        }),
      );

      // Sort by ranking score (descending)
      rankedResults.sort((a, b) => b.relevanceScore - a.relevanceScore);

      // Track ranking performance
      this.metrics.track('search_results_ranked', {
        query,
        results_count: results.length,
        model_version: this.currentModel.version,
        user_id: userId,
      });

      return rankedResults;
    } catch (error) {
      console.error('Error ranking search results:', error);

      // Fallback to original ordering
      this.metrics.track('search_ranking_error', {
        query,
        error: error instanceof Error ? error.message : 'Unknown error',
        results_count: results.length,
      });

      return results;
    }
  }

  /**
   * Update user behavior based on search result interactions
   */
  async trackUserInteraction(
    userId: string,
    resultId: string,
    interactionType: 'click' | 'dwell' | 'bounce',
    metadata: {
      query?: string;
      dwellTime?: number;
      position?: number;
    } = {},
  ): Promise<void> {
    try {
      // Store user interaction
      await supabase.from('search_user_interactions').insert({
        user_id: userId,
        result_id: resultId,
        interaction_type: interactionType,
        query: metadata.query,
        dwell_time: metadata.dwellTime,
        position: metadata.position,
        created_at: new Date().toISOString(),
      });

      // Update user profile if needed
      if (interactionType === 'click') {
        await this.updateUserProfile(userId, resultId, metadata);
      }

      // Track for analytics
      this.metrics.track('search_user_interaction', {
        user_id: userId,
        result_id: resultId,
        interaction_type: interactionType,
        query: metadata.query,
        dwell_time: metadata.dwellTime,
        position: metadata.position,
      });
    } catch (error) {
      console.error('Error tracking user interaction:', error);
    }
  }

  /**
   * Train/update the ranking model based on user behavior data
   */
  async updateRankingModel(): Promise<void> {
    if (this.isTraining) return;

    this.isTraining = true;

    try {
      // Get recent user interaction data
      const { data: interactions } = await supabase
        .from('search_user_interactions')
        .select('*')
        .gte(
          'created_at',
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        ) // Last 30 days
        .order('created_at', { ascending: false });

      if (!interactions || interactions.length < 100) {
        console.warn('Not enough interaction data for model training');
        return;
      }

      // Analyze interaction patterns
      const analysis = this.analyzeUserInteractions(interactions);

      // Update model weights based on analysis
      const updatedModel = this.optimizeModelWeights(analysis);

      // Validate new model performance
      const isImprovement = await this.validateModel(updatedModel);

      if (isImprovement) {
        this.currentModel = updatedModel;
        this.lastModelUpdate = new Date().toISOString();

        // Store updated model
        await this.saveModel(updatedModel);

        this.metrics.track('search_model_updated', {
          version: updatedModel.version,
          improvement_score: isImprovement,
          training_data_points: interactions.length,
        });
      }
    } catch (error) {
      console.error('Error updating ranking model:', error);
    } finally {
      this.isTraining = false;
    }
  }

  /**
   * Get personalized search suggestions based on user behavior
   */
  async getPersonalizedSuggestions(
    userId: string,
    limit: number = 5,
  ): Promise<string[]> {
    try {
      const userProfile = await this.getUserProfile(userId);
      if (!userProfile) return [];

      // Get suggestions based on user's search history and preferences
      const suggestions = userProfile.searchHistory
        .slice(0, limit * 2) // Get more than needed
        .filter((query, index, self) => self.indexOf(query) === index) // Remove duplicates
        .slice(0, limit); // Take only what we need

      return suggestions;
    } catch (error) {
      console.error('Error getting personalized suggestions:', error);
      return [];
    }
  }

  // Private methods

  private async calculateRankingFactors(
    result: SearchResult,
    query: string,
    userProfile: UserProfile | null,
  ): Promise<RankingFactors> {
    // Calculate content recency (0-1 scale)
    const recency = this.calculateRecencyScore(result.publishedDate);

    // Calculate authority score (0-1 scale)
    const authority = await this.calculateAuthorityScore(result.author);

    // Calculate engagement metrics
    const engagement = await this.calculateEngagementScore(result.id);

    // Calculate query relevance
    const relevance = this.calculateRelevanceScore(result, query);

    // Calculate popularity
    const popularity = await this.calculatePopularityScore(result.id);

    // Calculate user behavior metrics
    const clickThroughRate = await this.calculateCTRScore(result.id, query);
    const dwellTime = await this.calculateDwellTimeScore(result.id);
    const bounceRate = await this.calculateBounceRateScore(result.id);

    // Calculate personalization factors
    const typePreference = userProfile
      ? userProfile.contentTypePreferences[result.type] || 0.5
      : 0.5;

    // Calculate seasonality (tournaments, seasonal content)
    const seasonality = this.calculateSeasonalityScore(result);

    return {
      recency,
      authority,
      engagement,
      relevance,
      popularity,
      clickThroughRate,
      dwellTime,
      bounceRate,
      typePreference,
      seasonality,
    };
  }

  private calculateRankingScore(factors: RankingFactors): number {
    const weights = this.currentModel.weights;

    let score = 0;
    score += factors.recency * weights.recency;
    score += factors.authority * weights.authority;
    score += factors.engagement * weights.engagement;
    score += factors.relevance * weights.relevance;
    score += factors.popularity * weights.popularity;
    score += factors.clickThroughRate * weights.clickThroughRate;
    score += factors.dwellTime * weights.dwellTime;
    score += factors.bounceRate * weights.bounceRate; // Note: this can be negative
    score += factors.typePreference * weights.typePreference;
    score += factors.seasonality * weights.seasonality;

    // Ensure score is between 0 and 1
    return Math.max(0, Math.min(1, score));
  }

  private calculateRecencyScore(
    publishedDate?: string | null,
  ): number {
    if (!publishedDate) return 0.5;

    const now = new Date();
    const published = new Date(publishedDate);
    const daysDiff =
      (now.getTime() - published.getTime()) / (1000 * 60 * 60 * 24);

    // Recent content gets higher score, decay over time
    if (daysDiff <= 1) return 1.0;
    if (daysDiff <= 7) return 0.9;
    if (daysDiff <= 30) return 0.7;
    if (daysDiff <= 90) return 0.5;
    if (daysDiff <= 365) return 0.3;
    return 0.1;
  }

  private async calculateAuthorityScore(
    author?: string | null,
  ): Promise<number> {
    if (!author) return 0.5;

    // This could be enhanced with actual author metrics
    // For now, return a simulated score
    const authorScores: Record<string, number> = {
      'john-doe': 0.9,
      'jane-smith': 0.85,
      'alex-chen': 0.8,
      // Default for unknown authors
    };

    return authorScores[author] || 0.6;
  }

  private async calculateEngagementScore(resultId: string): Promise<number> {
    try {
      // Get engagement metrics from interactions table
      const { data: interactions } = await supabase
        .from('search_user_interactions')
        .select('interaction_type')
        .eq('result_id', resultId)
        .gte(
          'created_at',
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        );

      if (!interactions || interactions.length === 0) return 0.5;

      const clicks = interactions.filter(
        (i) => i.interaction_type === 'click',
      ).length;
      const total = interactions.length;

      return Math.min(1.0, clicks / total);
    } catch (error) {
      return 0.5; // Default score on error
    }
  }

  private calculateRelevanceScore(result: SearchResult, query: string): number {
    const queryTerms = query
      .toLowerCase()
      .split(' ')
      .filter((term) => term.length > 2);

    let score = 0;
    let matchCount = 0;

    // Check title matches (higher weight)
    const titleWords = (result.title || '').toLowerCase().split(' ');
    queryTerms.forEach((term) => {
      if (titleWords.some((word) => word.includes(term))) {
        score += 0.4;
        matchCount++;
      }
    });

    // Check excerpt matches
    const excerptWords = (result.excerpt || '').toLowerCase().split(' ');
    queryTerms.forEach((term) => {
      if (excerptWords.some((word) => word.includes(term))) {
        score += 0.2;
        matchCount++;
      }
    });

    // Check tag matches
    queryTerms.forEach((term) => {
      if (result.tags?.some((tag) => tag.toLowerCase().includes(term))) {
        score += 0.3;
        matchCount++;
      }
    });

    // Normalize by number of query terms
    return queryTerms.length > 0
      ? Math.min(1.0, score / queryTerms.length)
      : 0.5;
  }

  private async calculatePopularityScore(resultId: string): Promise<number> {
    // This would typically come from page views, social shares, etc.
    // For now, simulate based on recent clicks
    try {
      const { data: clicks, count } = await supabase
        .from('search_user_interactions')
        .select('*', { count: 'exact', head: true })
        .eq('result_id', resultId)
        .eq('interaction_type', 'click')
        .gte(
          'created_at',
          new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        );

      // Normalize click count to 0-1 scale (assuming max 100 clicks per week is very popular)
      return Math.min(1.0, (count || 0) / 100);
    } catch (error) {
      return 0.5;
    }
  }

  private async calculateCTRScore(
    resultId: string,
    query: string,
  ): Promise<number> {
    try {
      // Get impressions vs clicks for this result and query
      const { data: impressions, count: totalImpressions } = await supabase
        .from('search_analytics')
        .select('*', { count: 'exact', head: true })
        .eq('result_id', resultId)
        .eq('query', query);

      const { data: clicks, count: totalClicks } = await supabase
        .from('search_user_interactions')
        .select('*', { count: 'exact', head: true })
        .eq('result_id', resultId)
        .eq('query', query)
        .eq('interaction_type', 'click');

      if (!totalImpressions || totalImpressions === 0) return 0.5;

      const ctr = (totalClicks || 0) / totalImpressions;
      return Math.min(1.0, ctr * 10); // Normalize assuming 10% CTR is excellent
    } catch (error) {
      return 0.5;
    }
  }

  private async calculateDwellTimeScore(resultId: string): Promise<number> {
    try {
      const { data: dwellTimes } = await supabase
        .from('search_user_interactions')
        .select('dwell_time')
        .eq('result_id', resultId)
        .eq('interaction_type', 'dwell')
        .not('dwell_time', 'is', null);

      if (!dwellTimes || dwellTimes.length === 0) return 0.5;

      const avgDwellTime =
        (dwellTimes as { dwell_time: number | null }[]).reduce(
          (sum, d) => sum + (d.dwell_time || 0),
          0,
        ) / dwellTimes.length;

      // Normalize assuming 300 seconds (5 minutes) is excellent dwell time
      return Math.min(1.0, avgDwellTime / 300);
    } catch (error) {
      return 0.5;
    }
  }

  private async calculateBounceRateScore(resultId: string): Promise<number> {
    try {
      const { data: interactions } = await supabase
        .from('search_user_interactions')
        .select('interaction_type')
        .eq('result_id', resultId)
        .in('interaction_type', ['click', 'bounce']);

      if (!interactions || interactions.length === 0) return 0.5;

      const bounces = interactions.filter(
        (i) => i.interaction_type === 'bounce',
      ).length;
      const clicks = interactions.filter(
        (i) => i.interaction_type === 'click',
      ).length;

      if (clicks === 0) return 0.5;

      const bounceRate = bounces / clicks;
      return 1.0 - bounceRate; // Invert because lower bounce rate is better
    } catch (error) {
      return 0.5;
    }
  }

  private calculateSeasonalityScore(result: SearchResult): number {
    // This would be enhanced with tournament schedules, game releases, etc.
    const now = new Date();
    const month = now.getMonth(); // 0-11

    // Boost tournament content during typical esports seasons
    if (result.type === 'tournament') {
      // Spring (Mar-May) and Fall (Sep-Nov) are typically big tournament seasons
      if ((month >= 2 && month <= 4) || (month >= 8 && month <= 10)) {
        return 0.8;
      }
      return 0.6;
    }

    // Default seasonality score
    return 0.5;
  }

  private async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data: profile } = (await supabase
        .from('user_search_profiles')
        .select('*')
        .eq('user_id', userId)
        .single()) as { data: { profile_data: string } | null };

      return profile ? (JSON.parse(profile.profile_data) as UserProfile) : null;
    } catch (error) {
      return null;
    }
  }

  private async updateUserProfile(
    userId: string,
    resultId: string,
    metadata: { query?: string },
  ): Promise<void> {
    // Implementation would update user preferences based on click behavior
    // This is simplified for the example
    console.log(
      `Updating user profile for ${userId} based on click on ${resultId}`,
    );
  }

  private analyzeUserInteractions(
    interactions: unknown[],
  ): InteractionAnalysis {
    // Analyze click patterns, dwell times, bounce rates, etc.
    // Return insights for model optimization
    return {
      avgCTR: 0.1,
      avgDwellTime: 180,
      avgBounceRate: 0.3,
      topPerformingFactors: ['relevance', 'recency', 'authority'],
    };
  }

  private optimizeModelWeights(analysis: InteractionAnalysis): RankingModel {
    // Use the analysis to adjust model weights
    // This could use more sophisticated ML algorithms
    const newWeights = { ...this.currentModel.weights };

    // Simplified optimization logic
    if (analysis.avgCTR < 0.05) {
      newWeights.relevance += 0.02;
      newWeights.authority += 0.01;
    }

    return {
      ...this.currentModel,
      weights: newWeights,
      version: `${parseFloat(this.currentModel.version) + 0.1}`,
      lastUpdated: new Date().toISOString(),
    };
  }

  private async validateModel(model: RankingModel): Promise<number> {
    // Test new model against holdout data
    // Return improvement score (positive = better, negative = worse)
    return 0.05; // Simulated 5% improvement
  }

  private async saveModel(model: RankingModel): Promise<void> {
    try {
      await supabase.from('search_ranking_models').upsert({
        version: model.version,
        weights: model.weights,
        created_at: model.lastUpdated,
      });
    } catch (error) {
      console.error('Error saving model:', error);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'search-ranking': SearchRankingService;
  }
}
